// Locked notes (docs/plans/trilium-import.md §3): end-to-end encryption, done on the device before anything syncs.
// A passphrase → scrypt → key-encryption key, which wraps one random 256-bit data key per person (stored as the
// "keyring" in meta/notesLock). The data key seals each locked note's title, body and versions with AES-256-GCM.
// Changing the passphrase re-wraps the data key; no note is re-encrypted. Forgetting it loses the notes — by design.
// Pure JS (@noble), so it runs the same on the phone (Hermes, over OTA), the web and Node.
import { scryptAsync } from '@noble/hashes/scrypt';
import { gcm } from '@noble/ciphers/aes';
import { htmlToText } from './notes.js';

export const LOCK_KDF = { alg: 'scrypt', N: 2 ** 15, r: 8, p: 1 };
export const PASSPHRASE_MIN = 8;
export const LOCKED_TITLE = 'Locked note';
export const LOCKED_PREVIEW_MAX = 300;   // the start of the text, sealed, so unlocked lists can show a preview
const PREFIX = 'v1.';

// ---- bytes <-> text (no TextEncoder / atob: not every JS engine the app runs on has them) ------

export function utf8Encode(s) {
  const out = [];
  for (const ch of String(s)) {
    let c = ch.codePointAt(0);
    if (c < 0x80) out.push(c);
    else if (c < 0x800) out.push(0xc0 | (c >> 6), 0x80 | (c & 63));
    else if (c < 0x10000) out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63));
    else { out.push(0xf0 | (c >> 18), 0x80 | ((c >> 12) & 63), 0x80 | ((c >> 6) & 63), 0x80 | (c & 63)); }
  }
  return Uint8Array.from(out);
}
export function utf8Decode(b) {
  let s = '';
  for (let i = 0; i < b.length;) {
    const x = b[i++];
    let c;
    if (x < 0x80) c = x;
    else if (x < 0xe0) c = ((x & 31) << 6) | (b[i++] & 63);
    else if (x < 0xf0) c = ((x & 15) << 12) | ((b[i++] & 63) << 6) | (b[i++] & 63);
    else c = ((x & 7) << 18) | ((b[i++] & 63) << 12) | ((b[i++] & 63) << 6) | (b[i++] & 63);
    s += String.fromCodePoint(c);
  }
  return s;
}
const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const B64I = Object.fromEntries([...B64].map((c, i) => [c, i]));
export function b64encode(b) {
  let s = '';
  for (let i = 0; i < b.length; i += 3) {
    const n = (b[i] << 16) | ((b[i + 1] ?? 0) << 8) | (b[i + 2] ?? 0);
    s += B64[(n >> 18) & 63] + B64[(n >> 12) & 63] + (i + 1 < b.length ? B64[(n >> 6) & 63] : '=') + (i + 2 < b.length ? B64[n & 63] : '=');
  }
  return s;
}
export function b64decode(s) {
  const clean = String(s).replace(/=+$/, '');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let o = 0;
  for (let i = 0; i < clean.length; i += 4) {
    const n = (B64I[clean[i]] << 18) | ((B64I[clean[i + 1]] ?? 0) << 12) | ((B64I[clean[i + 2]] ?? 0) << 6) | (B64I[clean[i + 3]] ?? 0);
    if (o < out.length) out[o++] = (n >> 16) & 255;
    if (o < out.length) out[o++] = (n >> 8) & 255;
    if (o < out.length) out[o++] = n & 255;
  }
  return out;
}

/** Secure random bytes (crypto.getRandomValues — the phone polyfills it from expo-crypto). */
export function randomBytes(n) {
  const c = globalThis.crypto;
  if (!c?.getRandomValues) throw new Error('no secure random source on this device');
  return c.getRandomValues(new Uint8Array(n));
}

// ---- sealing -------------------------------------------------------------------------------

export const isSealed = (s) => typeof s === 'string' && s.startsWith(PREFIX);

export function sealBytes(key, bytes) {
  const iv = randomBytes(12);
  return `${PREFIX}${b64encode(iv)}.${b64encode(gcm(key, iv).encrypt(bytes))}`;
}
export function openBytes(key, sealed) {
  if (!isSealed(sealed)) throw new Error('not a sealed value');
  const [iv, ct] = sealed.slice(PREFIX.length).split('.');
  try { return gcm(key, b64decode(iv)).decrypt(b64decode(ct)); } catch { throw new Error('can’t open — wrong key or damaged'); }
}
export const sealText = (key, text) => sealBytes(key, utf8Encode(text ?? ''));
export const openText = (key, sealed) => utf8Decode(openBytes(key, sealed));

// ---- the keyring (meta/notesLock) ------------------------------------------------------------

async function kek(passphrase, kdf) {
  return scryptAsync(utf8Encode(String(passphrase).normalize('NFKC')), b64decode(kdf.salt), { N: kdf.N, r: kdf.r, p: kdf.p, dkLen: 32, asyncTick: 20 });
}

/** A new keyring for a passphrase → { keyring (the doc to store), key (the data key, keep in memory only) }. */
export async function createKeyring(passphrase, { kdf = LOCK_KDF, now = Date.now() } = {}) {
  if (String(passphrase || '').length < PASSPHRASE_MIN) throw new Error(`use at least ${PASSPHRASE_MIN} characters`);
  const k = { ...kdf, salt: b64encode(randomBytes(16)) };
  const key = randomBytes(32);
  const wrapped = sealBytes(await kek(passphrase, k), key);
  return { keyring: { v: 1, kdf: k, wrapped, createdAt: now, changedAt: now }, key };
}

/** The data key, or throws "wrong passphrase". */
export async function openKeyring(keyring, passphrase) {
  if (!keyring?.wrapped) throw new Error('locked notes aren’t set up yet');
  try { return openBytes(await kek(passphrase, keyring.kdf), keyring.wrapped); } catch { throw new Error('wrong passphrase'); }
}

/** Same data key under a new passphrase (and a new salt). */
export async function rewrapKeyring(keyring, oldPassphrase, newPassphrase, { now = Date.now() } = {}) {
  if (String(newPassphrase || '').length < PASSPHRASE_MIN) throw new Error(`use at least ${PASSPHRASE_MIN} characters`);
  const key = await openKeyring(keyring, oldPassphrase);
  const k = { ...keyring.kdf, salt: b64encode(randomBytes(16)) };
  return { ...keyring, kdf: k, wrapped: sealBytes(await kek(newPassphrase, k), key), changedAt: now };
}

// ---- a locked note ---------------------------------------------------------------------------
// Meta doc: `locked: true`, `sealedTitle`, and blank title / content / refs (so nothing readable syncs); tags, date,
// notebook, section, links and pins stay in the clear so it still files and sorts. Body chunk: { html: '', sealed }.

/** What a locked note's meta doc carries for a title + body (replaces bodyMeta's readable fields). The preview (the
 *  start of the text) is sealed too, so a list can show it only while the notes are unlocked. */
export function lockedMeta(key, { title, html }) {
  const out = { locked: true, content: '', words: null, refs: [], hasChecklist: false, hasImages: false, bodyBytes: String(html ?? '').length };
  if (html !== undefined) out.sealedPreview = sealText(key, htmlToText(html).slice(0, LOCKED_PREVIEW_MAX));
  if (title !== undefined) Object.assign(out, { title: '', sealedTitle: sealText(key, title) });
  return out;
}

/** A body chunk (or a History version) sealed. */
export const sealBodyData = (key, { html, ...rest }) => ({ ...rest, html: '', sealed: sealText(key, html ?? '') });

/** The readable html of a body chunk / version: plain → as is; sealed → opened with the key; null without it. */
export function bodyHtml(data, key) {
  if (!data) return '';
  if (!isSealed(data.sealed)) return data.html ?? '';
  return key ? openText(key, data.sealed) : null;
}

/** The note as lists show it: a locked note's title opened when the key is here, else "Locked note". */
export function displayNote(n, key) {
  if (!n?.locked) return n;
  if (key && isSealed(n.sealedTitle)) {
    try {
      const content = isSealed(n.sealedPreview) ? openText(key, n.sealedPreview) : '';
      return { ...n, title: openText(key, n.sealedTitle), content, lockedOpen: true };
    } catch { /* another key */ }
  }
  return { ...n, title: LOCKED_TITLE, lockedOpen: false };
}
